import { RadioGroup } from 'component-library';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RadioItem } from '@/components/widgets/a/RadioItem/RadioItem';

export const CHART_ZOOM = {
  DEFAULT: 'default',
  BENCHMARK: 'benchmark',
  TIME_SERIES: 'time_series',
  MOUSE_WHEEL: 'mouse_wheel',
} as const;

export type ZoomValue = (typeof CHART_ZOOM)[keyof typeof CHART_ZOOM];

export type ChartZoomStateHook = {
  zoom: ZoomValue;
  setZoom: Dispatch<SetStateAction<ZoomValue>>;
};

export const useChartZoom: () => ChartZoomStateHook = () => {
  const [zoom, setZoom] = useState<ZoomValue>(CHART_ZOOM.DEFAULT);

  return { zoom, setZoom } as const;
};

export const ChartZoom = ({ setZoom, zoom }: { setZoom?: Dispatch<SetStateAction<ZoomValue>>; zoom?: ZoomValue }) => {
  const { t } = useTranslation();

  const isDisabled = !setZoom || !zoom;

  const handleValueChange = (newZoom: string | boolean | null | undefined) => {
    if (typeof newZoom !== 'string') return;

    setZoom?.(newZoom as ZoomValue);
  };

  return (
    <RadioGroup
      onValueChange={handleValueChange}
      data-testid='zoom-radio-buttons'
      onClick={(e) => e.stopPropagation()}
      className='gap-0'
      disabled={isDisabled}
    >
      <RadioItem
        label={t('graph.zoom.radioLabel.title.default')}
        value={CHART_ZOOM.DEFAULT}
        isChecked={zoom === CHART_ZOOM.DEFAULT}
      />
      <RadioItem
        label={t('graph.zoom.radioLabel.title.benchmark')}
        value={CHART_ZOOM.BENCHMARK}
        isChecked={zoom === CHART_ZOOM.BENCHMARK}
        tooltip={t('graph.zoom.radioLabel.description.benchmark')}
      />
      <RadioItem
        label={t('graph.zoom.radioLabel.title.time_series')}
        value={CHART_ZOOM.TIME_SERIES}
        isChecked={zoom === CHART_ZOOM.TIME_SERIES}
        tooltip={t('graph.zoom.radioLabel.description.time_series')}
      />
    </RadioGroup>
  );
};
